import React from 'react';
import { ReactFlow, Background, useReactFlow, addEdge, MiniMap } from '@xyflow/react';
import { useNavigate } from 'react-router-dom';
import '@xyflow/react/dist/style.css';
import './style.css';

import AgeRangeNode from './node/AgeRangeNode';
import AgeRangeContextMenu from './context-menu/AgeRangeContextMenu';
import { AddNewHouseDialog } from './dialog/AddNewHouseDialog';
import { HouseNode } from './node/HouseNode';
import HouseContextMenu from './context-menu/HouseContextMenu';
import { Sidebar } from './Sidebar';
import { useAccomodationDiagram } from './AccomodationDiagramContext';
import { BedNode } from './node/BedNode';
import { EventNode } from './node/EventNode';
import toast from 'react-hot-toast';
import BedContextMenu from './context-menu/BedContextMenu';
import EventContextMenu from './context-menu/EventContextMenu';
import { OrderDetailsDialog } from './dialog/OrderDetailsDialog';
import { calculateAge, extractTimeFromISOString, formatDateTime } from '../../../services/functions';
import { Button, Stack, Typography } from '@mui/material';
import { RelatedOrdersMenuDialog } from './dialog/RelatedOrdersMenuDialog';
import { ManageRelatedOrdersGroupDialog } from './dialog/ManageRelatedOrdersGroupDialog';
import { CommentsDialog } from './dialog/CommentsDialog';
import { changeAccommodationOwnershipAction, getManagementReport } from '../../../services/request';
import { useMutation } from 'react-fetching-library';
import { EventRecordDialog } from './dialog/EventRecordDialog';
import { EditHouseDialog } from './dialog/EditHouseDialog';
import { LeftSidebar } from './LeftSidebar';
import { DiagramDataSyncDialog } from './dialog/DiagramDataSyncDialog';
import { OwnershipDialog } from './dialog/OwnershipDialog';
import { useApp } from '../../../AppContext';
import { ChanegAgeRangeDialog } from './dialog/ChanegAgeRangeDialog';
import { AccommodationPrefixDialog } from './dialog/AccommodationPrefixDialog';
import { OrderAccommodationPrefixDialog } from './dialog/OrderAccommodationPrefixDialog';
import { checkStudioGender, isStudio } from './helpers';

const nodeTypes = {
  AgeRangeNode,
  HouseNode,
  BedNode,
  EventNode
};

export const AccomodationDiagramDetails = ({ selectedAccomodationDiagramParams, setSelectedAccomodationDiagram: setChoiceSelectedAccomodationDiagram, params }) => {
  const navigate = useNavigate();
  const { screenToFlowPosition } = useReactFlow();
  const { mutate, loading } = useMutation(getManagementReport);
  const { mutate: changeOwnershipMutate } = useMutation(changeAccommodationOwnershipAction);
  const [menu, setMenu] = React.useState(null);
  const [openNewHouseDialog, setOpenNewHouseDialog] = React.useState(false);
  const [openEditHouseDialog, setOpenEditHouseDialog] = React.useState(false);
  const [ageRangeNodeData, setAgeRangeNodeData] = React.useState(null);
  const ref = React.useRef(null);
  const { order, orders, nodes, setNodes, onNodesChange, updateNodeF, addNodesF,
    removeNode, selectedAccomodationDiagram, setSelectedAccomodationDiagram,
    showOrderDetails, setShowOrderDetails, showRelatedOrdersMenu, setShowRelatedOrdersMenu,
    setOrders, showManageRelatedOrdersMenu, setShowManageRelatedOrdersMenu, edges, setEdges,
    onEdgesChange, showComments, setShowComments, setRecalculateHousePositions,
    showEventRecordGroupMenu, setShowEventRecordGroupMenu, eventRecord,
    selectedEventGroup, isSaving, updateNodesF, setSearchText, setSelectedEventGroup,
    houseOrders, setHouseOrders, showDiagramDataSyncDialog, setShowDiagramDataSyncDialog,
    showOwnershipDialog, setShowOwnershipDialog, savingDisabled, setSavingDisabled, setQueryOrdersSignal,
    showChangeAgeRangeDialog, setShowChangeAgeRangeDialog, setInitOrders,
    showAccommodationPrefixDialog, setShowAccommodationPrefixDialog,
    showOrderAccommodationPrefixDialog, setShowOrderAccommodationPrefixDialog } = useAccomodationDiagram();
  const { ownershipHash } = useApp();

  React.useEffect(() => {
    if (selectedAccomodationDiagramParams) {
      setSelectedAccomodationDiagram(selectedAccomodationDiagramParams);
    }
  }, [selectedAccomodationDiagramParams]);

  const [nodesLoaded, setNodesLoaded] = React.useState(false);
  const [contextNode, setContextNode] = React.useState();

  const handleDownloadComprehensiveSalesPlanReport = async () => {
    const { error: mutateError, payload: mutatePayload } = await mutate(selectedAccomodationDiagram.id);
    if (!mutateError) {
      const url = window.URL.createObjectURL(mutatePayload);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style = 'display: none';
      a.href = url;
      const currentDate = new Date();
      const formattedDate = currentDate.toISOString().slice(0, 10); // Formatowanie na 'YYYY-MM-DD'
      const fileName = `Raport Kierowniczy - ${formattedDate}.xlsx`;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      toast.error('Nie udało się pobrać raportu');
    }
  };

  const onNodeContextMenu = React.useCallback(
    (event, node) => {
      event.preventDefault();

      const pane = ref.current.getBoundingClientRect();
      const menuWidth = 150; // Szerokość menu
      let menuHeight = 100; // Wysokość menu
      if (node.type == 'BedNode') {
        menuHeight = 300;
      } else if (node.type == 'HouseNode') {
        menuHeight = 600;
      }

      let top = event.clientY - pane.top;
      let left = event.clientX - pane.left;

      // Sprawdź, czy menu wykracza poza prawą krawędź
      if (left + menuWidth > pane.width) {
        left = pane.width - menuWidth;
      }

      // Sprawdź, czy menu wykracza poza dolną krawędź
      if (top + menuHeight > pane.height) {
        top = pane.height - menuHeight;
      }

      // Sprawdź, czy menu wykracza poza lewą krawędź
      if (left < 0) {
        left = 0;
      }

      // Sprawdź, czy menu wykracza poza górną krawędź
      if (top < 0) {
        top = 0;
      }

      // Ustaw pozycję menu
      setMenu({
        id: node.id,
        type: node.type,
        top,
        left
      });
      setContextNode(node);
    },
    [setMenu, ref, setContextNode]
  );

  // Close the context menu if it's open whenever the window is clicked.
  const onPaneClick = React.useCallback(() => {
    setMenu(null);
    setContextNode();
  }, [setMenu]);

  const [highlightedNodeId, setHighlightedNodeId] = React.useState(null); // Zapisz ID zaznaczonego node'a
  const highlightedNodeIdRef = React.useRef(null);

  const onDragOver = React.useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    // Znajdź element pod kursorem myszki
    const elementUnderCursor = document.elementFromPoint(event.clientX, event.clientY);

    // Sprawdź, czy to element node'a (np. ma odpowiednią klasę CSS)
    if (elementUnderCursor && elementUnderCursor.closest('.react-flow__node')) {
      const nodeElement = elementUnderCursor.closest('.react-flow__node');

      // Odczytaj ID elementu
      const nodeId = nodeElement.getAttribute('data-id');
      const selectedNode = nodes.find(node => node.id === nodeId);

      if (selectedNode && selectedNode.type === 'BedNode') {
        // Sprawdź, czy node już jest zaznaczony
        if (highlightedNodeId !== nodeId) {
          setHighlightedNodeId(nodeId); // Zapisz ID zaznaczonego node'a
          highlightedNodeIdRef.current = nodeId;
          // Zmień styl node'a
          // selectedNode.style = {
          //   ...selectedNode.style,
          //   width: selectedNode.style.width * 1.2,
          //   height: selectedNode.style.height * 1.2
          // };
          // updateNodeF(selectedNode);
        }
      }
    }
  }, [highlightedNodeId, nodes, setNodes]);

  const onDragLeave = React.useCallback(() => {
    // Przywróć styl node'a po zakończeniu drag & drop
    if (highlightedNodeIdRef.current) {
      // const leftNode = nodes.find(node => node.id === highlightedNodeId);
      // leftNode.style = {
      //   ...leftNode.style,
      //   width: leftNode.style.width / 1.2,
      //   height: leftNode.style.height / 1.2
      // };
      setHighlightedNodeId(null); // Resetuj zaznaczenie node'a
      highlightedNodeIdRef.current = null;
      // updateNodeF(leftNode);
    }
  }, [highlightedNodeId, nodes, setNodes]);

  const validateOrderForNode = (node, house, order, showErrors = true) => {
    const errors = [];
    const houseAccommodatedBeds = nodes.filter(n => n.data.houseId == node.data.houseId && n.type == 'BedNode' && n.data.order);
    if (!node.data.eventId) {
      const message = 'To miejsce nie ma przypisanej imprezy';
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (node.data.order) {
      const message = 'To miejsce jest już zakwaterowane';
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (order.accommodation_event_group && node.data.eventGroup.id !== order.accommodation_event_group) {
      const message = `Zamówienie ${order.identifier} przypisane jest do innej grupy imprezowej`;
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (node.data.eventId !== order.event_id) {
      const message = `Próbujesz dodać zamówienie z imprezą: ${order.event__name} do miejsca przypisanego do imprezy: ${node.data.eventName}`;
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (house.data.preferredGender !== 'brak' && house.data.preferredGender !== order.participant_gender) {
      const message = `Ten domek jest przypisany dla ${house.data.preferredGender === 'boy' ? 'chłopców' : 'dziewczynek'}`;
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (
      isStudio(house, edges) && !checkStudioGender(house, nodes, edges, order)) {
      const message = 'Ten domek nie może posiadać uczestników różnej płci.';

      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (house.data.preferredAge !== 'brak' && house.data.preferredAge !== calculateAge(order.participant_birth_date, true, selectedAccomodationDiagram.camp_date_from)) {
      const message = `Ten domek jest przypisany dla ${house.data.preferredAge}-latków`;
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    } else if (house.data.preferredGender == 'brak' && houseAccommodatedBeds.length > 0 && houseAccommodatedBeds[0].data.order.participant_gender != order.participant_gender) {
      const message = 'W tym domku są już osoby innej płci';
      if (showErrors) {
        toast.error(message);
      } else {
        errors.push(message);
      }

      return { isValid: false, errors };
    }

    return { isValid: true, errors: [] };
  };

  const onDrop = React.useCallback(
    event => {
      event.preventDefault();
      if (highlightedNodeIdRef.current) {
        const nodesToUpdate = [];
        const selectedBed = nodes.find(node => node.id === highlightedNodeIdRef.current);
        const house = nodes.find(n => n.id === selectedBed.data.houseId);

        if (order.relatedGroup) {
          // Scenariusz 1 - zamówienie jest powiązane z innymi

          // Sprawdzamy czy pierwsze zamówienie spełnia wszystkie wymogi
          const { isValid: firstOrderValid } = validateOrderForNode(selectedBed, house, order);
          if (firstOrderValid) {
            nodesToUpdate.push(selectedBed);
            const errorsAgg = [];
            // Pierwsze zamówienie przeszło, sprawdzamy następne w pętli
            // Pobieramy wszystkie powiązane zamówienia
            const relatedOrders = orders.filter(o => o.relatedGroup && o.relatedGroup.id === order.relatedGroup.id).filter(o => o.id !== order.id);
            // Pobieramy pozostałe dostępne łóżka w tym domku
            let otherBeds = nodes.filter(n => n.data.houseId === selectedBed.data.houseId && n.type == 'BedNode').filter(n => n.id !== selectedBed.id);

            // Jeśli mamy grupę złożoną z conajmniej 6 osób to rozpatrujemy zakwaterowanie w domkach typu studio
            if (relatedOrders.length >= 5) {
              // Sprawdzamy czy ten domek nie jest połączony z innym (studio). Jeśli tak to rozszerzamy łóżka do wyboru
              const relatedEdges = edges.filter(edge => edge.source === house.id || edge.target === house.id);
              // Pobierz ID powiązanych node'ów
              const connectedHousesIds = relatedEdges.map(edge => edge.source === house.id ? edge.target : edge.source);
              // Teraz znajdź dane powiązanych node'ów
              const connectedBeds = nodes.filter(n => connectedHousesIds.includes(n.data.houseId) && n.type == 'BedNode');
              otherBeds = [...otherBeds, ...connectedBeds];
            }
            // Walidacja - sprawdzamy czy ilość
            relatedOrders.forEach(o => {
              if (otherBeds.length === 0) {
                errorsAgg.push([`Błędy zakwaterowania zamówienia powiązanego ${o.participant_name} - ${o.identifier}`, 'Brak wolnych łóżek']);
              }
              for (let i = 0; i < otherBeds.length; i++) {
                const bed = otherBeds[i];
                const { isValid, errors } = validateOrderForNode(bed, house, o, false);
                if (isValid) {
                  const updatedBed = { ...bed };
                  updatedBed.data = {
                    ...updatedBed.data,
                    order: o
                  };
                  nodesToUpdate.push(updatedBed);
                  otherBeds = otherBeds.filter(ob => ob.id !== bed.id); // Aktualizacja listy otherBeds
                  break; // Przerwij wewnętrzną pętlę po znalezieniu poprawnego łóżka
                } else {
                  errorsAgg.push([`Błędy zakwaterowania zamówienia powiązanego ${o.participant_name} - ${o.identifier}`, errors.join('\n')]);
                }
              }
            });
            // + 1 bo dodajemy jeszcze ten wybrany node
            if (nodesToUpdate.length === relatedOrders.length + 1) {
              selectedBed.data = {
                ...selectedBed.data,
                order
              };
              // updatujemy wszystkie node'y
              // nodesToUpdate.map(n => updateNodeF(n));
              updateNodesF(nodesToUpdate);
              // Wyrzucamy zamówienia z listy
              const ids = [order.id, ...nodesToUpdate.map(n => n.data.order.id)];
              setOrders(prevOrders => prevOrders.filter(po => !ids.includes(po.id)));
              setRecalculateHousePositions([house.id]);
            } else {
              errorsAgg.map(ea => toast.error(`${ea[0]}: \n ${ea[1]}`, { duration: 5000 }));
            }
          }

        } else {
          // Scenariusz 2 - zamówienie osobno. Zakwateruj miejsce zamówieniem
          const { isValid } = validateOrderForNode(selectedBed, house, order);

          if (isValid) {
            selectedBed.data = {
              ...selectedBed.data,
              order
            };
            toast.success(`Zakwaterowano uczestnika ${order.participant_name}`);
            setOrders(prevOrders => prevOrders.filter(po => po.id !== order.id));
            setRecalculateHousePositions([house.id]);
          }
        }
        // Bez względu na walidację musimy zupdetować styl node'a
        // selectedBed.style = {
        //   width: selectedBed.style.width / 1.2,
        //   height: selectedBed.style.height / 1.2
        // };
        setHighlightedNodeId(null); // Resetuj zaznaczenie node'a
        highlightedNodeIdRef.current = null;
        updateNodeF(selectedBed);
      }
    },
    [screenToFlowPosition, order, nodes, setNodes, updateNodeF, updateNodesF]
  );

  const onConnect = React.useCallback(
    params => setEdges(eds => addEdge({
      ...params,
      animated: true,
      style: { stroke: '#00AB55', strokeWidth: 2 } // Dostosowany kolor i grubość
    }, eds)),
    []
  );

  const takeControlCallback = async shouldTakeControl => {
    if (shouldTakeControl) {
      const { error, payload } = await changeOwnershipMutate({ ownershipHash, verify: false }, selectedAccomodationDiagram.id);
      if (!error) {
        setQueryOrdersSignal(true);
        setSelectedAccomodationDiagram(payload);
        setChoiceSelectedAccomodationDiagram(payload);
        setShowOwnershipDialog(false);
        setSavingDisabled(false);
      }
    } else {
      const { error, payload } = await changeOwnershipMutate({ onlyData: true }, selectedAccomodationDiagram.id);
      if (!error) {
        setQueryOrdersSignal(true);
        setSelectedAccomodationDiagram(payload);
        setChoiceSelectedAccomodationDiagram(payload);
        setShowOwnershipDialog(false);
        setSavingDisabled(true);
      }


    }
  };

  const closeDiagram = async () => {
    if (savingDisabled) {
      setChoiceSelectedAccomodationDiagram(null);
      setSavingDisabled(false);
      setOrders([]);
      setInitOrders(null);
      setSelectedEventGroup(null);
      setSearchText('');
      if (params) {
        navigate('/employee/accommodation/');
      }

      return;
    }
    const { error, payload } = await changeOwnershipMutate({ ownershipHash, verify: false, clearOwnershipHash: true }, selectedAccomodationDiagram.id);
    if (!error) {
      setChoiceSelectedAccomodationDiagram(null);
      setOrders([]);
      setInitOrders(null);
      setSelectedEventGroup(null);
      if (params) {
        navigate('/employee/accommodation/');
      }
    }
  };

  React.useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onPaneClick();
      }
    };

    // Dodaj listener klawiatury
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      // Usuń listener przy odmontowaniu komponentu
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onPaneClick]);

  React.useEffect(() => {
    if (!nodesLoaded && nodes.length > 0) {
      setNodesLoaded(true);
    }
  }, [nodes]);

  React.useEffect(async () => {
    if (params) {
      if (selectedAccomodationDiagramParams.diagram_data.ownershipHash && selectedAccomodationDiagramParams.diagram_data.ownershipHash !== ownershipHash) {
        setShowOwnershipDialog(true);
        setSavingDisabled(true);
      } else {
        const { error, payload } = await changeOwnershipMutate({ ownershipHash, verify: false }, selectedAccomodationDiagramParams.id);
        if (!error) {
          setSelectedAccomodationDiagram(payload);
          setShowOwnershipDialog(false);
          setSavingDisabled(false);
        }
      }
    }
  }, [params]);

  if (!nodesLoaded) {
    return null;
  }

  return (
    <div className="dndflow">
      {isSaving && <div className="overlay">Zapisywanie...</div>}
      <LeftSidebar closeDiagram={closeDiagram} />
      <Stack direction="column" width="100%" mt={-3}>
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          {savingDisabled && <Typography color="red">Podgląd ost. akt. {formatDateTime(selectedAccomodationDiagram.updated_at)}</Typography>}
          <Typography mb={0} color="primary" align="center">
            {selectedAccomodationDiagram.resort_name} - {selectedAccomodationDiagram.camp_name}
          </Typography>
          <Button variant="contained" onClick={handleDownloadComprehensiveSalesPlanReport} disabled={loading}>
            Raport kierowniczy
          </Button>
        </Stack>
        <ReactFlow
          nodeTypes={nodeTypes}
          nodes={nodes}
          edges={edges}
          onEdgesChange={onEdgesChange}
          fitView
          ref={ref}
          onPaneClick={onPaneClick}
          onNodeContextMenu={onNodeContextMenu}
          onNodesChange={onNodesChange}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onConnect={onConnect}
          nodesDraggable={savingDisabled ? false : true }
          elementsSelectable={savingDisabled ? false : true}
          nodesConnectable={savingDisabled ? false : true}
          snapToGrid={false}
          onlyRenderVisibleElements={true}
        >
          <Background />
          <MiniMap nodeStrokeWidth={3} zoomable pannable />
          {menu && menu.type === 'AgeRangeNode' && <AgeRangeContextMenu
            setAgeRangeNodeData={setAgeRangeNodeData}
            onPaneClick={onPaneClick}
            setOpenNewHouseDialog={setOpenNewHouseDialog}
            {...menu}
          />
          }
          {menu && menu.type === 'HouseNode' && <HouseContextMenu
            onPaneClick={onPaneClick}
            updateNode={updatedNode => updateNodeF(updatedNode)}
            removeNode={nodeId => removeNode(nodeId)}
            setOpenEditHouseDialog={setOpenEditHouseDialog}
            {...menu}
          />
          }
          {menu && menu.type === 'BedNode' && <BedContextMenu
            onPaneClick={onPaneClick}
            {...menu}
          />
          }
          {menu && menu.type === 'EventNode' && <EventContextMenu
            onPaneClick={onPaneClick}
            {...menu}
          />
          }
        </ReactFlow>
      </Stack>
      <Sidebar
      />
      {openNewHouseDialog && <AddNewHouseDialog
        ageRangeNodeData={ageRangeNodeData}
        open={openNewHouseDialog}
        setOpen={setOpenNewHouseDialog}
        addNodesF={addNodesF}
        allNodes={nodes}
        setRecalculateHousePositions={setRecalculateHousePositions}
      />}
      {openEditHouseDialog && contextNode && <EditHouseDialog
        open={openEditHouseDialog}
        setOpen={setOpenEditHouseDialog}
        node={contextNode}
      />}
      {showOrderDetails && <OrderDetailsDialog open={showOrderDetails} setOpen={setShowOrderDetails} order={order} />}
      {showRelatedOrdersMenu && <RelatedOrdersMenuDialog open={showRelatedOrdersMenu} setOpen={setShowRelatedOrdersMenu} order={order} orders={orders} setOrders={setOrders} houseOrders={houseOrders} setHouseOrders={setHouseOrders} nodes={nodes} />}
      {showManageRelatedOrdersMenu && <ManageRelatedOrdersGroupDialog open={showManageRelatedOrdersMenu} setOpen={setShowManageRelatedOrdersMenu} order={order} orders={orders} setOrders={setOrders} houseOrders={houseOrders} setHouseOrders={setHouseOrders} nodes={nodes} />}
      {showComments && <CommentsDialog open={showComments} setOpen={setShowComments} node={contextNode} />}
      {showEventRecordGroupMenu && <EventRecordDialog open={showEventRecordGroupMenu} setOpen={setShowEventRecordGroupMenu} eventRecord={eventRecord} selectedEventGroup={selectedEventGroup} />}
      {showDiagramDataSyncDialog && <DiagramDataSyncDialog open={showDiagramDataSyncDialog} setOpen={setShowDiagramDataSyncDialog} />}
      {showOwnershipDialog && <OwnershipDialog open={showOwnershipDialog} setOpen={setShowOwnershipDialog} takeControlCallback={takeControlCallback} />}
      {showChangeAgeRangeDialog && <ChanegAgeRangeDialog open={showChangeAgeRangeDialog} setOpen={setShowChangeAgeRangeDialog} node={contextNode} allNodes={nodes} onPaneClick={onPaneClick} />}
      {showAccommodationPrefixDialog && <AccommodationPrefixDialog open={showAccommodationPrefixDialog} setOpen={setShowAccommodationPrefixDialog} node={contextNode} />}
      {showOrderAccommodationPrefixDialog && <OrderAccommodationPrefixDialog open={showOrderAccommodationPrefixDialog} setOpen={setShowOrderAccommodationPrefixDialog} />}

    </div>
  );

};
